/**
 * Task title generation utility for frontend
 *
 * Provides a centralized way to generate human-readable task titles based on command types.
 * This ensures consistency with the backend task title generation.
 */

import { CommandType } from '~/protos/agents'

/**
 * Generate human-readable title based on command type
 */
export function generateTaskTitle(commandType: CommandType): string {
  // Map command types to human-readable titles (matching backend logic)
  const titleMap: Partial<Record<CommandType, string>> = {
    [CommandType.QUESTION_ANSWERING]: 'Single Repo Code Lens',
    [CommandType.GENERATE_IMAGE]: 'Diagram Generator',
    [CommandType.GENERATE_SIMPLE_CODE_PR]: 'Repository Coder',
    [CommandType.GENERATE_DOCUMENTATION]: 'Document Generator',
    [CommandType.GENERATE_FULL_REPO_TECH_MANIFEST_DOCUMENTATION]: 'Repository Docs Generator',
    [CommandType.AUTODESK_MULE_TO_NODE_JS_MIGRATION]: 'MuleSoft to Node.js Migration',
    [CommandType.GENERATE_MAPPING_TABLE]: 'Mapping Table Generator',
    [CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM]: 'Domain Diagram Generator',
    [CommandType.GENERATE_ADD_FLOW_CODE_PR]: 'Integration Generator',
    [CommandType.DATAWEAVE_TO_DATA_CREATION]: 'Sample Data Generator',
    [CommandType.MUNIT_TEST_CREATION]: 'MUnit Test Generator',
    [CommandType.GENERATE_DATAWEAVE]: 'DataWeave Generator',
    [CommandType.MULTI_REPO_QA]: 'Multi Repo Code Lens',
    [CommandType.GENERAL_MULTI_REPO_QA_DEBUG_AGENT]: 'Multi-Repo Q&A (Debug)',
    [CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION]: 'DataWeave Generator',
    [CommandType.MIGRATION_DOCUMENT]: 'Migration Generator',
    [CommandType.GENERATE_REPO_ADD_FLOW_CODE_PR]: 'Integration Generator',
    [CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE]: 'DataWeave Generator',
    [CommandType.GENERATE_DATAWEAVE_REPO_AWARE]: 'DataWeave Generator',
    [CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE]: 'Sample Generator',
    [CommandType.CODE_REVIEW]: 'Code Review Lens',
    [CommandType.MULESOFT_TO_CAMEL_MIGRATION]: 'MuleSoft to Camel Migration',
    [CommandType.MIGRATION_QUESTION_ANSWERING]: 'Migration Repo Code Lens',
    [CommandType.MULE_3_TO_MULE_4_MIGRATION]: 'Mule 3 to Mule 4 Migration',
    [CommandType.BILLABLE_FLOW_DOCUMENTATION]: 'Migration Inventory',
    [CommandType.MIGRATION_ACCELERATOR]: 'Migration Accelerator',
    [CommandType.MEGA_QA_AGENT_V1]: 'Agent Chat',
    [CommandType.CODE_REVIEW_API]: 'Code Review API',
    [CommandType.MULE_REFACTORING]: 'Mule Refactoring Agent',
    [CommandType.SPRING_INTEGRATION_REFACTORING]: 'Spring Integration Refactoring Agent',
    [CommandType.INTEGRATION_TEST]: 'Integration Testing Agent',
    [CommandType.MULE_VERSION_UPGRADE]: 'Mule Version Upgrades',
    [CommandType.MVN_CMD]: 'Maven Command Runner',
    [CommandType.NON_MULE_DOCUMENTATION]: 'Non-Mule Documentation',
    [CommandType.AZURE_DEPLOY_TEST]: 'Azure Migrate + Deploy'
  }

  return titleMap[commandType] || `Task ${commandType}`
}

/**
 * Resolve a conversation's display title: use the DB title if it has been
 * set by the agent, otherwise derive a fallback from the conversation mode.
 */
export function getConversationDisplayTitle(
  title: string | null | undefined,
  mode: string | null | undefined
): string {
  if (title?.trim()) return title
  if (mode) {
    const commandType = CommandType[mode as keyof typeof CommandType]
    if (commandType !== undefined) return generateTaskTitle(commandType)
  }
  return 'Agent Chat'
}

/**
 * Generate task title from command type string (for API responses)
 */
export function generate_task_title(cmdType: string): string {
  // Try to convert string to CommandType enum
  const commandType = CommandType[cmdType as keyof typeof CommandType]

  if (commandType !== undefined) {
    return generateTaskTitle(commandType)
  }

  // If not found in enum, return a formatted version of the string
  return cmdType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}
